import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputSwitch } from 'primereact/inputswitch';
import { FileUpload } from 'primereact/fileupload';

const EmployeeFormDialog = ({
  visible,
  onHide,
  editingEmployee,
  employeeForm,
  setEmployeeForm,
  departmentOptions,
  supervisors,
  submitting,
  handleSaveEmployee
}) => {
  const footer = (
    <div className="flex justify-between items-center mt-4">
      <Button label="Cancel" icon="pi pi-times" onClick={onHide} className="bg-black text-white rounded-lg px-4 py-2 hover:bg-gray-900 transition-all" disabled={submitting} />
      <Button label={editingEmployee ? 'Update' : 'Create'} icon="pi pi-check" onClick={handleSaveEmployee} loading={submitting} className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg px-4 py-2 shadow-md hover:shadow-lg transition-all" />
    </div>
  );

  return (
    <Dialog header={editingEmployee ? 'Edit Employee' : 'Add New Employee'} visible={visible} onHide={onHide} style={{ width: '800px' }} footer={footer}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
            <InputText value={employeeForm.firstName} onChange={(e) => setEmployeeForm({ ...employeeForm, firstName: e.target.value })} placeholder="Enter first name" className="w-full" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
            <InputText value={employeeForm.lastName} onChange={(e) => setEmployeeForm({ ...employeeForm, lastName: e.target.value })} placeholder="Enter last name" className="w-full" required />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
            <InputText value={employeeForm.email} onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })} placeholder="Enter email address" className="w-full" type="email" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
            <InputText value={employeeForm.phone}
              onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value.replace(/\D/g, '') })} placeholder="Enter phone number" className="w-full" required />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Joining Date *</label>
            <Calendar value={employeeForm.joiningDate} onChange={(e) => setEmployeeForm({ ...employeeForm, joiningDate: e.value })} showIcon className="w-full" dateFormat="dd-mm-yy" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Salary *</label>
            <InputNumber value={employeeForm.salary} onValueChange={(e) => setEmployeeForm({ ...employeeForm, salary: e.value })} placeholder="Enter salary" className="w-full" prefix="₹" min={0} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
            <Calendar value={employeeForm.dateOfBirth} onChange={(e) => setEmployeeForm({ ...employeeForm, dateOfBirth: e.value })} showIcon className="w-full" dateFormat="dd-mm-yy" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
            <Dropdown
              value={employeeForm.gender}
              options={[
                { label: 'Male', value: 'male' },
                { label: 'Female', value: 'female' },
                { label: 'Other', value: 'other' }
              ]}
              onChange={(e) => setEmployeeForm({ ...employeeForm, gender: e.value })} placeholder="Select gender" className="w-full" showClear />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Department *</label>
            <Dropdown value={employeeForm.departmentId} onChange={(e) => setEmployeeForm({ ...employeeForm, departmentId: e.value })} options={departmentOptions} placeholder="Select department" className="w-full" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Designation *</label>
            <InputText value={employeeForm.designation} onChange={(e) => setEmployeeForm({ ...employeeForm, designation: e.target.value })} placeholder="Enter designation" className="w-full" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Supervisor</label>
            <Dropdown value={employeeForm.supervisorEmpId} options={supervisors.map(emp => ({ label: `${emp.firstName || ''} ${emp.lastName || ''}`, value: emp.id }))}
              onChange={(e) => setEmployeeForm({ ...employeeForm, supervisorEmpId: e.value })} placeholder="Select supervisor" className="w-full" showClear />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Photo</label>
            <div className="flex items-center gap-4">
              {employeeForm.photoUrl && (
                <img 
                  src={employeeForm.photoUrl} 
                  alt="Preview" 
                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-200" 
                />
              )}
              <FileUpload 
                mode="basic" 
                name="photo" 
                accept="image/*" 
                maxFileSize={1000000} 
                className="w-full"
                auto
                chooseLabel={employeeForm.photoUrl ? "Change Photo" : "Upload Photo"}
                customUpload
                uploadHandler={async (e) => {
                  const file = e.files[0];
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setEmployeeForm({ ...employeeForm, photoUrl: reader.result });
                  };
                  reader.readAsDataURL(file);
                }}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
            <span className="text-sm font-semibold text-gray-700">PF Applicable</span>
            <InputSwitch 
              checked={employeeForm.pfApplicable || false} 
              onChange={e => setEmployeeForm({ ...employeeForm, pfApplicable: e.value })} 
            />
          </div>
          <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
            <span className="text-sm font-semibold text-gray-700">Allowance Applicable</span>
            <InputSwitch 
              checked={employeeForm.allowanceApplicable || false} 
              onChange={e => setEmployeeForm({ ...employeeForm, allowanceApplicable: e.value })} 
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
          <InputTextarea value={employeeForm.address} onChange={(e) => setEmployeeForm({ ...employeeForm, address: e.target.value })} placeholder="Enter address" rows={3} className="w-full" />
        </div>
      </div>
    </Dialog>
  );
};

export default EmployeeFormDialog;
